/* Persistent floating mini player card at the bottom of the screen. Shown when a track is active. */

import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import { MiniPlayerProgressBar } from "@/components/player/MiniPlayerProgressBar";

interface MiniPlayerProps {
  onExpand: () => void;
}

export function MiniPlayer({ onExpand }: MiniPlayerProps) {
  const { state, togglePlayPause, skipForward } = useAudioPlayer();
  const { track, playbackState, currentTime, duration } = state;

  return (
    <AnimatePresence>
      {track && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-40 pb-6 pointer-events-none"
        >
          <div className="mx-auto w-full max-w-[1200px] px-3 md:px-6">
            <div className="flex min-w-0">
              {/* Spacer matching sidebar width */}
              <div className="hidden lg:block w-64 shrink-0" />
              <div className="flex-1 min-w-0 lg:px-4">
                <div
                  className="rounded-2xl border border-border/50 shadow-xl overflow-hidden cursor-pointer pointer-events-auto"
                  style={{ backgroundColor: "color-mix(in oklch, var(--background) 85%, black)" }}
                  onClick={onExpand}
                >
                  <div className="flex items-center gap-3 px-4" style={{ height: 72 }}>
                    {/* Thumbnail */}
                    {track.thumbnail ? (
                      <img
                        src={track.thumbnail}
                        alt={track.title}
                        className="rounded-xl object-cover bg-muted shrink-0"
                        style={{ width: 52, height: 52 }}
                      />
                    ) : (
                      <div
                        className="rounded-xl bg-foreground/10 shrink-0 flex items-center justify-center"
                        style={{ width: 52, height: 52 }}
                      >
                        <Play className="size-5 text-muted-foreground" />
                      </div>
                    )}

                    {/* Title + source */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{track.title}</p>
                      {track.sourceName && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {track.sourceName}
                        </p>
                      )}
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-11 rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePlayPause();
                        }}
                      >
                        {playbackState === "playing" ? (
                          <Pause className="size-5 fill-current" />
                        ) : (
                          <Play className="size-5 fill-current" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          skipForward();
                        }}
                      >
                        <SkipForward className="size-4 fill-current" />
                      </Button>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="px-4 pb-1.5">
                    <MiniPlayerProgressBar currentTime={currentTime} duration={duration} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
